import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {TokenService} from '../../../../core/auth';
import {CreateUpdateWebhookRequest, Project, Webhook} from '../../../../core/models';
import {BreadcrumbService} from '../../../../breadcrumb.service';
import {ProjectSidebarService} from '../../../../projectsidebar.service';
import {AppComponent} from '../../../../app.component';
import {combineLatest} from 'rxjs';
import {map} from 'rxjs/operators';
import {SelectItem} from 'primeng/api';
import {WebhookService} from '../../../../core/services';


@Component({
  selector: 'app-project-webhook-details',
  templateUrl: './project-webhook-details.page.html'
})
export class ProjectWebhookDetailsPage implements OnInit {



  authUser = this.token.whoAmI();
  loading = false;
  busy = false;
  error = false;

  addMode = true;

  availableProtocols: SelectItem[];
  selectedProtocol: any;
  availableMethods: SelectItem[];
  selectedMethod: any;
  macros: any;
  bodyPlaceholder: string = '{\n'
      + '  "id": "{{id}}",\n'
      + '  "created": "{{created}}",\n'
      + '  "eventType": "{{eventType}}",\n'
      + '  "eventDescription": "{{eventDescription}}",\n'
      + '  "playerId": "{{playerId}}",\n'
      + '  "transactionId": "{{transactionId}}",\n'
      + '  "externalProjectId": "{{externalProjectId}}",\n'
      + '  "customerData": "{{customerData}}"\n'
    + '}';

  projectId: string;
  project: Project;
  webhookId: string;
  webhook: Webhook;

  testResponse: string = null;

  constructor(
      private cdRef: ChangeDetectorRef,
      private app: AppComponent,
      private route: ActivatedRoute,
      private router: Router,
      private token: TokenService,
      private breadcrumbService: BreadcrumbService,
      private projectSidebarService: ProjectSidebarService,
      private webhookService: WebhookService
  ) {
  }


  ngOnInit() {
    this.error = false;
    this.loading = true;

    combineLatest(new Array(this.route.parent.data, this.route.params, this.webhookService.getAllMacros()))
        .pipe(map(results => ({
          project: results[0].project,
          projectId: results[0].project.id,
          webhookId: results[1].webhookId,
          macros: results[2]
        })))
        .subscribe(result => {
              this.project = result.project;
              this.projectId = result.projectId;
              this.webhookId = result.webhookId;
              this.addMode = !this.webhookId;

              this.macros = result.macros;

              this.projectSidebarService.setProject(this.project);

              if (!this.addMode) {
                this.webhookService.getOne(this.projectId, this.webhookId).subscribe(
                    (data) => {
                      this.webhook = data;

                      this.initProtocolSelectItems(this.webhook.protocol);
                      this.initMethodSelectItems(this.webhook.method);

                      this.breadcrumbService.setItems([
                        {label: 'projects', routerLink: ['/project']},
                        {label: this.project.name, routerLink: ['/project', this.project.id, 'dashboard']},
                        {label: 'webhooks', routerLink: ['/project', this.project.id, 'webhooks']},
                        {label: this.webhookId, routerLink: ['/project', this.project.id, 'webhook', this.webhookId]},
                      ]);

                      this.busy = false;
                      this.loading = false;
                      this.cdRef.detectChanges();
                    },
                    () => {
                      this.error = true;
                      this.loading = false;
                      this.cdRef.detectChanges();
                    }
                );
              } else {
                this.webhook = new Webhook();
                this.webhook.protocol = 'HTTPS';
                this.webhook.method = 'GET';

                this.initProtocolSelectItems(this.webhook.protocol);
                this.initMethodSelectItems(this.webhook.method);

                this.breadcrumbService.setItems([
                  {label: 'projects', routerLink: ['/project']},
                  {label: this.project.name, routerLink: ['/project', this.project.id, 'dashboard']},
                  {label: 'webhooks', routerLink: ['/project', this.project.id, 'webhooks']},
                  {label: 'add', routerLink: ['/project', this.project.id, 'webhook']},
                ]);

                this.busy = false;
                this.error = false;
                this.loading = false;
                this.cdRef.detectChanges();
              }

            },
            () => {
              this.error = false;
              this.loading = false;
              this.cdRef.detectChanges();
            });
  }

  initProtocolSelectItems(value) {
    this.availableProtocols = [
      {label: 'HTTP', value: 'HTTP'},
      {label: 'HTTPS', value: 'HTTPS'}
    ];
    if (value) {
      this.selectedProtocol = this.availableProtocols.filter(x => x.value === value)[0].value;
    } else {
      this.selectedProtocol = this.availableProtocols[0].value;
    }
  }

  initMethodSelectItems(value) {
    this.availableMethods = [
      {label: 'GET', value: 'GET'},
      {label: 'POST', value: 'POST'},
      {label: 'PUT', value: 'PUT'},
      {label: 'PATCH', value: 'PATCH'},
      {label: 'DELETE', value: 'DELETE'}
    ];
    if (value) {
      this.selectedMethod = this.availableMethods.filter(x => x.value === value)[0].value;
    } else {
      this.selectedMethod = this.availableMethods[0].value;
    }
  }

  updateWebhookParameters(event: any) {
    this.webhook.webhookParameters = event;
  }

  updateWebhookHeaders(event: any) {
    this.webhook.webhookHeaders = event;
  }

  getMacros() {
    let macroList = '';

    if (this.macros) {
      this.macros.forEach(macro => macroList = macroList + '\n' + macro.macroValue);
    }
    return macroList;
  }

  saveWebhook() {
    this.busy = true;

    if (this.addMode) {
        const req = new CreateUpdateWebhookRequest(
            this.webhook.path,
            this.selectedProtocol,
            this.selectedMethod,
            this.webhook.body,
            this.webhook.webhookHeaders,
            this.webhook.webhookParameters);

      this.webhookService.create(this.projectId, req)
          .subscribe(
              (_) => {
                this.busy = false;
                this.navigateToOverview();
              },
              (error) => {
                this.busy = false;
              }
          );
    } else {
      const req = new CreateUpdateWebhookRequest(
          this.webhook.path,
          this.selectedProtocol,
          this.selectedMethod,
          this.webhook.body,
          this.webhook.webhookHeaders,
          this.webhook.webhookParameters);
      this.webhookService.update(this.projectId, this.webhook.id, req)
          .subscribe(
              (_) => {
                this.busy = false;
                this.navigateToOverview();
              },
              (error) => {
                this.busy = false;
              }
          );
    }
  }

  verify() {
    return this.webhook.path;
  }

  navigateToOverview() {
    this.router.navigate(['project', this.projectId, 'webhooks']);
  }

  deleteWebhook() {
    this.webhookService.delete(this.projectId, this.webhookId).subscribe(
        () => {
          this.navigateToOverview();
          this.busy = false;
        },
        () => {
          this.busy = false;
        }
    );
  }

  getUrlPreview() {
    if (this.webhook.path) {
      let urlPreview = this.selectedProtocol.toLowerCase() + '://' + this.webhook.path;

      let first = true;
      if (this.webhook.webhookParameters) {
        this.webhook.webhookParameters.forEach(p => {
          urlPreview = urlPreview + (first ? '?' : '&') + p.key + '=' + p.value;
          first = false;
        });
      }
      return urlPreview;
    } else {
      return 'N/A';
    }
  }

  test() {
    this.testResponse = '';
      const req = new CreateUpdateWebhookRequest(
          this.webhook.path,
          this.selectedProtocol,
          this.selectedMethod,
          this.webhook.body,
          this.webhook.webhookHeaders,
          this.webhook.webhookParameters);

    this.webhookService.test(this.projectId, req).subscribe(
        (response) => {
          let responseText = '';
          if (response.statusCode !== null && response.statusCode !== undefined) {
            responseText += response.statusCode + '\n\n';
          }
          if (response.message !== null && response.message !== undefined) {
            responseText += response.message + '\n\n';
          }
          if (response.body !== null && response.body !== undefined) {
            responseText += response.body;
          }
          this.testResponse = responseText;
          this.busy = false;
        },
        () => {
          this.busy = false;
        }
    );
  }

}
